/* eslint-disable camelcase */
import {
	EVINCED_SEVERITIES_IDS,
	OptionType,
	SORT_ORDER,
	TFilterOptions
} from '@evinced-private/ui-common';

import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Mfa_Session_RunsSortFields,
	Mfa_Session_RunsSortOrder,
	Mfa_Session_RunsTextSearchFields
} from 'src/common/types/graphql.generated';
import { ITableQueryParams } from 'src/common/types/TableQueryTypes';

import { MfaSessionsQueryVariables } from '../MfaSessions.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';

const SEVERITY_FILTER_MAP: Record<EVINCED_SEVERITIES_IDS, string> = {
	[EVINCED_SEVERITIES_IDS.CRITICAL]: 'criticalIssuesFilter',
	[EVINCED_SEVERITIES_IDS.SERIOUS]: 'seriousIssuesFilter',
	[EVINCED_SEVERITIES_IDS.MODERATE]: 'moderateIssuesFilter',
	[EVINCED_SEVERITIES_IDS.MINOR]: 'minorIssuesFilter',
	[EVINCED_SEVERITIES_IDS.BEST_PRACTICE]: 'bestPracticeIssuesFilter',
	[EVINCED_SEVERITIES_IDS.NEEDS_REVIEW]: 'needsReviewIssuesFilter'
};

const sessionsTextSearchFields: Mfa_Session_RunsTextSearchFields[] = [
	Mfa_Session_RunsTextSearchFields.SessionName,
	Mfa_Session_RunsTextSearchFields.RunTime,
	Mfa_Session_RunsTextSearchFields.CriticalIssues,
	Mfa_Session_RunsTextSearchFields.TotalIssues,
	Mfa_Session_RunsTextSearchFields.UserName,
	Mfa_Session_RunsTextSearchFields.App,
	Mfa_Session_RunsTextSearchFields.AppVersion,
	Mfa_Session_RunsTextSearchFields.Os,
	Mfa_Session_RunsTextSearchFields.CustomFields
];

const sessionsSortFieldNamesMap: Record<string, Mfa_Session_RunsSortFields> = {
	sessionName: Mfa_Session_RunsSortFields.SessionName,
	runTime: Mfa_Session_RunsSortFields.RunTime,
	lastModified: Mfa_Session_RunsSortFields.RunTime,
	criticalIssues: Mfa_Session_RunsSortFields.CriticalIssues,
	totalIssues: Mfa_Session_RunsSortFields.TotalIssues,
	totalScans: Mfa_Session_RunsSortFields.TotalScans,
	createdBy: Mfa_Session_RunsSortFields.UserName,
	owner: Mfa_Session_RunsSortFields.UserName
};

const sessionsSortOrderMap: Record<SORT_ORDER, Mfa_Session_RunsSortOrder> = {
	[SORT_ORDER.ASC]: Mfa_Session_RunsSortOrder.Ascending,
	[SORT_ORDER.DESC]: Mfa_Session_RunsSortOrder.Descending
};

const buildSeverityFilterVariables = (
	severityOptions?: OptionType[]
): Partial<MfaSessionsQueryVariables> => {
	if (severityOptions?.length !== 1) {
		return {};
	}

	const severityValue = severityOptions[0].value;
	const filterKey = SEVERITY_FILTER_MAP[severityValue];

	return filterKey ? { [filterKey]: { value: [0] } } : {};
};

export const buildMfaSessionsQueryVariables = ({
	pageSize,
	page,
	sort,
	filterOptions
}: ITableQueryParams): MfaSessionsQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MfaSessionsQueryVariables>({
		sortFieldsMap: sessionsSortFieldNamesMap,
		sortOrderMap: sessionsSortOrderMap,
		searchFields: sessionsTextSearchFields
	});

	// Build core query parameters
	const searchValues = preprocessSearchValue(filterOptions?.searchValue ?? '');

	variablesBuilder
		.addPaginationParams(page, pageSize)
		.addSortParam(sort)
		.addTextSearchParam(searchValues);

	// Add dedicated filter fields
	if (filterOptions?.owner?.length) {
		variablesBuilder.addFilterParam('createdByFilter', filterOptions.owner);
	}

	if (filterOptions?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filterOptions.platform);
	}

	if (filterOptions?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filterOptions.appName);
	}

	if (filterOptions?.versions?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filterOptions.versions);
	}

	const knownFields = new Set([
		'owner',
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'versions',
		'severity'
	]);
	const customFields: TFilterOptions = {};

	if (filterOptions) {
		for (const [key, value] of Object.entries(filterOptions)) {
			if (!knownFields.has(key) && value && Array.isArray(value)) {
				customFields[key] = value;
			}
		}
	}

	// Handle custom fields if any exist
	if (Object.keys(customFields).length > 0) {
		variablesBuilder.addTupleFilterParam(
			'customFieldsFilter',
			customFields,
			ConditionOperators.All
		);
	}

	// Handle period filter
	const selectedPeriod = filterOptions?.sessionsPeriod?.[0];
	if (selectedPeriod?.value !== 'all') {
		variablesBuilder.addConditionParam(
			DAYS_AMOUNT_FILTER_PARAM_KEY,
			filterOptions.sessionsPeriod?.map((p) => Number(p.value))
		);
	}

	const variables = variablesBuilder.getVariables();
	const severityFilterVariables = buildSeverityFilterVariables(filterOptions?.severity);

	return { ...variables, ...severityFilterVariables };
};
